import { mdiCheck, mdiClose } from '@mdi/js';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Chip, IChipProps } from 'common/components/Chip';
import { Icon } from 'common/components/Icon';
import {
    ICertificateValidationStatus,
    IContentTypeValue,
    IPortfolioItem,
    IPortfolioItemScoreTypeChoices,
    Maybe,
} from 'graphql/types';
import { formatGrade } from 'training/utils/formatGrade';
import { CircularProgress } from 'common/components/CircularProgress';
import { ReceivedCertificateProgressChip } from 'common/components/ReceivedCertificateProgressChip';
import { SkillProgressChip } from 'skill/components/SkillProgressChip';
import { withSxProp } from 'common/utils/props';

interface IProps {
    portfolioItem?: Maybe<
        Pick<
            IPortfolioItem,
            | 'relatedType'
            | 'score'
            | 'scoreType'
            | 'scoreThreshold'
            | 'progress'
            | 'startDate'
            | 'endDate'
            | 'completionDate'
        > &
            Partial<Pick<IPortfolioItem, 'isCompliant'>> & {
                scoreMax?: Maybe<number>;
                relatedObjectHasComments?: boolean;
                relatedObjectHasFiles?: boolean;
            }
    >;
    hover?: boolean;
    isMandatory?: boolean;
    receivedCertificateValidationStatus?: ICertificateValidationStatus;
    size?: 'small' | 'normal' | 'large';
    sx?: SxProps;
    withIcon?: boolean;
    onClick?(): void;
}

export const PortfolioItemProgressScoreChip = ({
    portfolioItem,
    hover,
    isMandatory,
    receivedCertificateValidationStatus,
    size = 'normal',
    sx,
    withIcon,
    onClick,
}: IProps) => {
    const [translate] = useTranslation();

    const {
        relatedType,
        score,
        scoreType,
        scoreThreshold,
        scoreMax,
        progress,
        startDate,
        endDate,
        completionDate,
        relatedObjectHasComments,
        relatedObjectHasFiles,
    } = portfolioItem || {};

    let sizeStyle = {};
    let circularProgressChipSize = 28;

    if (size === 'small') {
        sizeStyle = {
            width: 23,
            height: 23,
            minWidth: 0,
        };
    } else if (size === 'large') {
        sizeStyle = {
            width: 40,
            height: 40,
            minWidth: 0,
            borderRadius: '50%',
        };

        circularProgressChipSize = 40;
    }

    if (withIcon) {
        sizeStyle = {
            ...sizeStyle,
            '.MuiChip-icon': {
                mx: 'auto',
                color: 'common.white',
                fontSize: size === 'large' ? 22 : 16,
            },
            '.MuiChip-label': { display: 'none' },
        };
    }

    const showDot = !!(relatedObjectHasComments || relatedObjectHasFiles);

    if (!portfolioItem) {
        return (
            <Chip
                aria-label={!!hover ? `${translate('progress')} 0%` : undefined}
                hover={hover}
                showDot={showDot}
                sx={[
                    ...withSxProp(sx),
                    { backgroundColor: 'grey.200', ...sizeStyle },
                ]}
            />
        );
    }

    let scoreValue: React.ReactNode;
    const scoreChipProps: IChipProps = {};

    if (
        relatedType === IContentTypeValue.ReceivedCertificate ||
        relatedType === IContentTypeValue.Certificate
    ) {
        return (
            <ReceivedCertificateProgressChip
                endDate={endDate}
                isMandatory={isMandatory}
                startDate={startDate}
                sx={[...withSxProp(sx), sizeStyle]}
                validationStatus={receivedCertificateValidationStatus}
                withIcon={withIcon}
                onClick={onClick}
            />
        );
    }

    if (relatedType === IContentTypeValue.Skill) {
        return (
            <SkillProgressChip
                hover={hover}
                portfolioItem={{
                    ...portfolioItem,
                    scoreMax: scoreMax ?? null,
                    relatedObjectHasComments: !!relatedObjectHasComments,
                    relatedObjectHasFiles: !!relatedObjectHasFiles,
                }}
                size={size}
                sx={sx}
                withIcon={withIcon}
                onClick={onClick}
            />
        );
    }

    const ariaLabel = !!onClick || hover ? translate('progress') : undefined;

    // If progress is not 1 and we don't have a completion date, show a circular progress icon
    if (progress !== 1 && !completionDate) {
        const progressValue = Math.round((progress || 0) * 100);

        return (
            <CircularProgress
                filled
                showTooltip
                aria-label={`${ariaLabel} ${progressValue}%`}
                bgColor="grey.200"
                color="inherit"
                hover={hover}
                showDot={showDot}
                size={circularProgressChipSize}
                sx={sx}
                tabIndex={!!onClick ? 0 : -1}
                value={progressValue}
                variant="determinate"
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    let status;

    if (score !== undefined && score !== null) {
        const showScoreValue =
            score !== 0 &&
            scoreType !== null &&
            scoreType !== IPortfolioItemScoreTypeChoices.Fulfilled;

        if (!showScoreValue) {
            scoreValue = undefined;
        } else if (relatedType === IContentTypeValue.Assignment) {
            scoreValue = formatGrade(score * 10);
        } else {
            scoreValue = `${Math.round(score * 100)}%`;
        }

        let chipColor: IChipProps['color'];

        // Check score color based on if we have a scoreThreshold, scoreMax or score
        if (scoreThreshold) {
            const isSuccess = score >= scoreThreshold;

            chipColor = isSuccess ? 'success' : 'error';
            status = translate(isSuccess ? 'fulfilled' : 'notFulfilled');
        } else if (scoreMax === 0) {
            chipColor = 'success';
            status = translate('fulfilled');
        } else {
            chipColor = score ? 'success' : 'error';
            status = translate(score ? 'fulfilled' : 'notFulfilled');
        }

        scoreChipProps.color = chipColor;

        if (withIcon) {
            scoreChipProps.icon =
                chipColor === 'error' ? (
                    <Icon path={mdiClose} size="2rem" />
                ) : (
                    <Icon path={mdiCheck} size="2rem" />
                );
        }
    } else {
        // If scoreType is not set, we show a dark grey chip
        scoreChipProps.sx = {
            backgroundColor: !scoreType ? 'grey.600' : 'grey.200',
        };
    }

    return (
        <Chip
            aria-label={`${translate('score')} ${scoreValue || status}`}
            sx={[...withSxProp(sx), sizeStyle]}
            {...scoreChipProps}
            hover={hover}
            label={scoreValue}
            showDot={!scoreValue && showDot}
            tabIndex={!!onClick ? 0 : -1}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onClick?.();
            }}
        />
    );
};
